// hooks/usePerformanceMonitor.js
import { useEffect, useCallback, useRef } from 'react';

/**
 * Performance monitoring hook for Core Web Vitals and custom metrics
 * - Tracks LCP, FID, CLS, FCP, TTFB
 * - Custom timing measurements
 * - Memory usage monitoring
 * - Network information
 */
export const usePerformanceMonitor = (options = {}) => {
  const {
    enableCoreWebVitals = true,
    enableCustomMetrics = true,
    enableMemoryMonitoring = true,
    enableNetworkMonitoring = true,
    onMetric = null,
    componentName = 'Unknown'
  } = options;

  const metricsRef = useRef({});
  const observerRef = useRef(null);

  // Core Web Vitals tracking
  useEffect(() => {
    if (!enableCoreWebVitals || typeof window === 'undefined') return;

    const reportMetric = (metric) => {
      metricsRef.current[metric.name] = metric.value;
      
      if (onMetric) {
        onMetric({
          ...metric,
          componentName,
          timestamp: Date.now()
        });
      }

      // Log to console in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`📊 [Performance] ${componentName} - ${metric.name}:`, metric.value);
      }
    };

    // Largest Contentful Paint (LCP)
    const observeLCP = () => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          
          reportMetric({
            name: 'LCP',
            value: lastEntry.startTime,
            unit: 'ms',
            rating: lastEntry.startTime < 2500 ? 'good' : lastEntry.startTime < 4000 ? 'needs-improvement' : 'poor'
          });
        });
        
        observer.observe({ entryTypes: ['largest-contentful-paint'] });
        observerRef.current = observer;
      }
    };

    // First Input Delay (FID)
    const observeFID = () => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            reportMetric({
              name: 'FID',
              value: entry.processingStart - entry.startTime,
              unit: 'ms',
              rating: entry.processingStart - entry.startTime < 100 ? 'good' : 
                     entry.processingStart - entry.startTime < 300 ? 'needs-improvement' : 'poor'
            });
          });
        });
        
        observer.observe({ entryTypes: ['first-input'] });
      }
    };

    // Cumulative Layout Shift (CLS)
    const observeCLS = () => {
      if ('PerformanceObserver' in window) {
        let clsValue = 0;
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          
          reportMetric({
            name: 'CLS',
            value: clsValue,
            unit: 'score',
            rating: clsValue < 0.1 ? 'good' : clsValue < 0.25 ? 'needs-improvement' : 'poor'
          });
        });
        
        observer.observe({ entryTypes: ['layout-shift'] });
      }
    };

    // First Contentful Paint (FCP)
    const observeFCP = () => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            if (entry.name === 'first-contentful-paint') {
              reportMetric({
                name: 'FCP',
                value: entry.startTime,
                unit: 'ms',
                rating: entry.startTime < 1800 ? 'good' : entry.startTime < 3000 ? 'needs-improvement' : 'poor'
              });
            }
          });
        });
        
        observer.observe({ entryTypes: ['paint'] });
      }
    };

    // Time to First Byte (TTFB)
    const measureTTFB = () => {
      if ('performance' in window && 'getEntriesByType' in performance) {
        const navigation = performance.getEntriesByType('navigation')[0];
        if (navigation) {
          const ttfb = navigation.responseStart - navigation.requestStart;
          reportMetric({
            name: 'TTFB',
            value: ttfb,
            unit: 'ms',
            rating: ttfb < 800 ? 'good' : ttfb < 1800 ? 'needs-improvement' : 'poor'
          });
        }
      }
    };

    // Initialize observers
    observeLCP();
    observeFID();
    observeCLS();
    observeFCP();
    measureTTFB();

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [enableCoreWebVitals, onMetric, componentName]);

  // Memory monitoring
  useEffect(() => {
    if (!enableMemoryMonitoring || typeof window === 'undefined' || !('memory' in performance)) return;

    const monitorMemory = () => {
      const memory = performance.memory;
      const memoryMetric = {
        name: 'Memory',
        value: {
          used: Math.round(memory.usedJSHeapSize / 1048576), // MB
          total: Math.round(memory.totalJSHeapSize / 1048576), // MB
          limit: Math.round(memory.jsHeapSizeLimit / 1048576) // MB
        },
        unit: 'MB',
        componentName,
        timestamp: Date.now()
      };

      metricsRef.current.memory = memoryMetric.value;

      if (onMetric) {
        onMetric(memoryMetric);
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`🧠 [Memory] ${componentName}:`, memoryMetric.value);
      }
    };

    const memoryInterval = setInterval(monitorMemory, 10000); // Every 10 seconds

    return () => clearInterval(memoryInterval);
  }, [enableMemoryMonitoring, onMetric, componentName]);

  // Network monitoring
  useEffect(() => {
    if (!enableNetworkMonitoring || typeof window === 'undefined' || !('connection' in navigator)) return;

    const connection = navigator.connection;
    
    const reportNetworkInfo = () => {
      const networkMetric = {
        name: 'Network',
        value: {
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt,
          saveData: connection.saveData
        },
        componentName,
        timestamp: Date.now()
      };

      metricsRef.current.network = networkMetric.value;

      if (onMetric) {
        onMetric(networkMetric);
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`📡 [Network] ${componentName}:`, networkMetric.value);
      }
    };

    // Initial report
    reportNetworkInfo();

    // Listen for network changes
    connection.addEventListener('change', reportNetworkInfo);

    return () => {
      connection.removeEventListener('change', reportNetworkInfo);
    };
  }, [enableNetworkMonitoring, onMetric, componentName]);

  // Custom timing measurement
  const startTiming = useCallback((name) => {
    if (!enableCustomMetrics) return;
    
    performance.mark(`${componentName}-${name}-start`);
  }, [enableCustomMetrics, componentName]);

  const endTiming = useCallback((name) => {
    if (!enableCustomMetrics) return;
    
    const endMark = `${componentName}-${name}-end`;
    const startMark = `${componentName}-${name}-start`;
    
    performance.mark(endMark);
    
    try {
      performance.measure(`${componentName}-${name}`, startMark, endMark);
      
      const measure = performance.getEntriesByName(`${componentName}-${name}`)[0];
      
      const timingMetric = {
        name: `Custom-${name}`,
        value: measure.duration,
        unit: 'ms',
        componentName,
        timestamp: Date.now()
      };

      metricsRef.current[`custom-${name}`] = timingMetric.value;

      if (onMetric) {
        onMetric(timingMetric);
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`⏱️ [Timing] ${componentName} - ${name}:`, `${measure.duration.toFixed(2)}ms`);
      }

      // Clean up marks and measures
      performance.clearMarks(startMark);
      performance.clearMarks(endMark);
      performance.clearMeasures(`${componentName}-${name}`);
      
    } catch (error) {
      console.warn(`Failed to measure timing for ${name}:`, error);
    }
  }, [enableCustomMetrics, onMetric, componentName]);

  // Get current metrics
  const getMetrics = useCallback(() => {
    return { ...metricsRef.current };
  }, []);

  // Export metrics for debugging
  const exportMetrics = useCallback(() => {
    const metrics = getMetrics();
    const exportData = {
      componentName,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      metrics
    };

    // In development, log to console
    if (process.env.NODE_ENV === 'development') {
      console.log('📋 [Metrics Export]', exportData);
    }

    return exportData;
  }, [getMetrics, componentName]);

  return {
    startTiming,
    endTiming,
    getMetrics,
    exportMetrics
  };
};

// Performance budget checker
export const usePerformanceBudget = (budgets = {}) => {
  const defaultBudgets = {
    LCP: 2500,      // ms
    FID: 100,       // ms
    CLS: 0.1,       // score
    FCP: 1800,      // ms
    TTFB: 800,      // ms
    bundleSize: 500 // KB
  };

  const finalBudgets = { ...defaultBudgets, ...budgets };

  const checkBudget = useCallback((metric) => {
    const budget = finalBudgets[metric.name];
    if (!budget) return null;

    const isWithinBudget = metric.value <= budget;
    const percentage = (metric.value / budget) * 100;

    return {
      metric: metric.name,
      value: metric.value,
      budget,
      isWithinBudget,
      percentage: Math.round(percentage),
      status: isWithinBudget ? 'pass' : 'fail'
    };
  }, [finalBudgets]);

  return { checkBudget, budgets: finalBudgets };
};